import React from 'react';
import { clientModuleRegistry } from '../../modules/registry.js';
import { useEnabledModules } from '../../modules/useEnabledModules.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import type { ClientWidgetContribution } from '../../modules/registry.js';

// ---------------------------------------------------------------------------
// Internal: renders one lazy widget inside Suspense
// ---------------------------------------------------------------------------

interface WidgetSlotProps {
  contribution: ClientWidgetContribution;
}

const WidgetSlot: React.FC<WidgetSlotProps> = ({ contribution }) => {
  const LazyWidget = React.useMemo(() => React.lazy(contribution.lazy), [contribution.lazy]);

  return (
    <React.Suspense
      fallback={
        <div className="rounded-xl bg-surface border p-4 flex items-center justify-center min-h-[120px]">
          <span className="text-sm text-muted animate-pulse">Loading…</span>
        </div>
      }
    >
      <LazyWidget />
    </React.Suspense>
  );
};

// ---------------------------------------------------------------------------
// Internal: policy-guarded widget slot
// Applied when the contribution declares a `policy` requirement.
// ---------------------------------------------------------------------------

interface GuardedWidgetSlotProps {
  contribution: ClientWidgetContribution;
}

const GuardedWidgetSlot: React.FC<GuardedWidgetSlotProps> = ({ contribution }) => {
  // Policy hook must be called unconditionally — it is always called, but
  // we skip rendering when the policy check fails.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const allowed = usePolicy(contribution.policy!);
  if (!allowed) return null;
  return <WidgetSlot contribution={contribution} />;
};

// ---------------------------------------------------------------------------
// Public: WidgetGrid
// Iterates enabled modules, collects widget contributions matching `surface`,
// applies policy gating, and renders a responsive grid.
// ---------------------------------------------------------------------------

export interface WidgetGridProps {
  /** Dashboard surface key, e.g. 'dashboard.trading'. */
  surface: string;
}

export const WidgetGrid: React.FC<WidgetGridProps> = ({ surface }) => {
  const { data: enabledManifests, isLoading } = useEnabledModules();

  if (isLoading || !enabledManifests) return null;

  const enabledIds = new Set(enabledManifests.map((m) => m.id));

  // Collect all widgets from enabled modules that are suitable for this surface
  const widgets: ClientWidgetContribution[] = [];
  for (const id of enabledIds) {
    const mod = clientModuleRegistry.get(id);
    if (!mod?.widgets) continue;
    for (const widget of mod.widgets) {
      // suitableFor undefined or empty means "all surfaces"
      const matchesSurface =
        !widget.suitableFor || widget.suitableFor.length === 0 || widget.suitableFor.includes(surface);
      if (matchesSurface) {
        widgets.push(widget);
      }
    }
  }

  if (widgets.length === 0) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {widgets.map((widget) =>
        widget.policy ? (
          <GuardedWidgetSlot key={widget.id} contribution={widget} />
        ) : (
          <WidgetSlot key={widget.id} contribution={widget} />
        ),
      )}
    </div>
  );
};
